// frontend/src/services/haptics.ts
//
// Haptic feedback for the native app; silent no-op on web (the plugin's web
// implementation vibrates on some Androids in the browser — we don't want
// that, so we gate on native explicitly). Fire-and-forget: callers never
// await these, and failures are swallowed — feedback must never break a flow.
import { isNativePlatform } from '@/services/platform';
import { Preferences } from '@capacitor/preferences';

type ImpactStyle = 'light' | 'medium' | 'heavy';
const HAPTICS_ENABLED_KEY = 'app.haptics.enabled';
let cachedHapticsEnabled: boolean | null = null;

const prefersReducedMotion = (): boolean => (
  typeof window !== 'undefined'
  && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
);

export async function getHapticsEnabled(): Promise<boolean> {
  if (cachedHapticsEnabled !== null) return cachedHapticsEnabled;
  try {
    if (isNativePlatform()) {
      const { value } = await Preferences.get({ key: HAPTICS_ENABLED_KEY });
      cachedHapticsEnabled = value !== 'false';
    } else {
      cachedHapticsEnabled = window.localStorage.getItem(HAPTICS_ENABLED_KEY) !== 'false';
    }
  } catch {
    cachedHapticsEnabled = true;
  }
  return cachedHapticsEnabled;
}

export async function setHapticsEnabled(enabled: boolean): Promise<void> {
  if (isNativePlatform()) {
    await Preferences.set({ key: HAPTICS_ENABLED_KEY, value: String(enabled) });
  } else {
    window.localStorage.setItem(HAPTICS_ENABLED_KEY, String(enabled));
  }
  cachedHapticsEnabled = enabled;
}

async function withHaptics(fn: (mod: typeof import('@capacitor/haptics')) => Promise<void>) {
  if (!isNativePlatform() || prefersReducedMotion() || !(await getHapticsEnabled())) return;
  try {
    await fn(await import('@capacitor/haptics'));
  } catch {
    /* device without haptics / plugin unavailable */
  }
}

/** Physical tap — selection changes, picks, toggles. */
export function hapticImpact(style: ImpactStyle = 'light'): void {
  void withHaptics(async ({ Haptics, ImpactStyle }) => {
    const map = { light: ImpactStyle.Light, medium: ImpactStyle.Medium, heavy: ImpactStyle.Heavy };
    await Haptics.impact({ style: map[style] });
  });
}

/** Success buzz — lineup locked, pick made, save confirmed. */
export function hapticSuccess(): void {
  void withHaptics(async ({ Haptics, NotificationType }) => {
    await Haptics.notification({ type: NotificationType.Success });
  });
}

/** Attention buzz — you're on the clock, time running out. */
export function hapticWarning(): void {
  void withHaptics(async ({ Haptics, NotificationType }) => {
    await Haptics.notification({ type: NotificationType.Warning });
  });
}
